import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useGuidedRegContext } from '../context/guidedRegContext';
import {
    ACCOUNT,
    ACCOUNT_TYPE,
    BUSINESS_ACCOUNT,
    CCA_PATH,
    CONTINUE,
    CREDIT,
    EMAIL,
    EMAIL_VERIFY,
    LOOKS_LIKE,
    NON_CREDIT,
    PAYMENT_PREFERENCE_CREDIT,
    PAYMENT_PREFERENCE_TYPE,
    PERSONAL_ACCOUNT,
    POD_PATH,
    SUCCESS_CODES,
    USER_EXISTS
} from '../constants';
import { logGRError, navigateToPath, refreshTokenCall, resendVerifyCode } from '../utils/guidedUtil';
import { EMAIL_REGEX } from '../../global/utils/commonUtils';
import { logError } from '../../global/utils/logger';
import { CREATE_ACCOUNT_DATA } from '../context/reducer';
import { duplicateProfiles } from '../api/apiRequest';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../constants/analyticsConstants/Ecommerce';
import { useAnalyticsContext } from '../../../config/GoogleTagManagerEvents';
import { STORAGE_CONFIG } from '../../../constants/storageConfig';
import { isPODFlow } from '../../cca/util/ccaUtil';

const useCreateAccount = () => {
    const navigate = useNavigate();
    const intl = useIntl();
    const [{ emailId, accountType }, guidedRegDispatch] = useGuidedRegContext();
    const [isLoading, setIsLoading] = useState(false);
    const [formInteracted, setFormInteracted] = useState(false);
    const [showFreeEmailModal, setShowFreeEmailModal] = useState(false);
    const [formState, setFormState] = useState({
        error: {},
        data: {
            [EMAIL]: emailId,
            [ACCOUNT_TYPE]: accountType
        }
    });

    const {
        sendEventsForGuidedRegistrationFormStarted,
        sendEventsForGuidedRegistrationFormError,
        sendEventsForGuidedRegistrationFormSubmited,
        sendEventsForGuidedRegistrationFormCompleted
    } = useAnalyticsContext();

    const createAccountErrorMsg = {
        accountTypeError: intl.formatMessage({ id: 'account_type_error' }),
        enterEmail: intl.formatMessage({ id: 'enter_email' }),
        validEmail: intl.formatMessage({ id: 'valid_email' }),
        userAlreadyExist: intl.formatMessage({ id: 'user_already_exists' })
    };

    // Function to track form started event
    const trackFormStartedEvent = fieldName => {
        if (!formInteracted) {
            sendEventsForGuidedRegistrationFormStarted(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_STARTED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_FORM_NAME,
                fieldName === ACCOUNT_TYPE ? ACCOUNT : fieldName
            );
            setFormInteracted(true);
        }
    };

    const saveFormDataState = (name, value) => {
        setFormState(prevState => ({
            ...prevState,
            data: {
                ...prevState.data,
                [name]: value
            },
            error: {
                ...prevState.error,
                [name]: ''
            }
        }));
    };

    const saveFormErrorState = (name, error) => {
        setFormState(prevState => ({
            ...prevState,
            error: {
                ...prevState.error,
                [name]: error
            }
        }));
    };

    const handleInputChange = event => {
        const { name, value } = event.target;
        saveFormDataState(name, value);
        trackFormStartedEvent(name);
    };

    const handleFocus = field => {
        saveFormErrorState(field, '');
        saveFormErrorState(USER_EXISTS, '');
        trackFormStartedEvent(field);
    };

    const handleSubmit = e => {
        e.preventDefault();
    };

    const getEmailError = email => {
        return !email
            ? createAccountErrorMsg.enterEmail
            : !EMAIL_REGEX.test(email)
            ? createAccountErrorMsg.validEmail
            : null;
    };

    const getAccountTypeError = accountType => {
        return !accountType ? createAccountErrorMsg.accountTypeError : null;
    };

    const checkDuplicateProfile = async email => {
        try {
            await refreshTokenCall();
            const { data, status, error } = await duplicateProfiles(email);
            return { data, status, error };
        } catch (error) {
            logError(error);
            return { data: null, error: error, status: null };
        }
    };

    const sendErrorEvent = errorMessage => {
        sendEventsForGuidedRegistrationFormError(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_ERROR,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_FORM_NAME,
            errorMessage // Pass the error message here
        );
    };

    const sendSubmitEvent = accountType => {
        sendEventsForGuidedRegistrationFormSubmited(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_SUBMITTED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_FORM_NAME,
            accountType
        );
    };

    const handleFreeEmailContinue = async () => {
        setShowFreeEmailModal(false);
        setIsLoading(true);
        const email = formState.data?.email;
        const response = await resendVerifyCode(email, EMAIL);
        try {
            if (response.status === SUCCESS_CODES.SUCCESS_CODE_201) {
                const payload = { email, accountType };
                guidedRegDispatch({ type: CREATE_ACCOUNT_DATA, payload: payload });
                navigateToPath(navigate, EMAIL_VERIFY);

                sendEventsForGuidedRegistrationFormCompleted(
                    EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_COMPLETED,
                    EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_FORM_NAME,
                    accountType
                );
                setIsLoading(false);
            } else if (response.error) {
                throw response.error;
            }
        } catch (error) {
            logGRError(error);
            setIsLoading(false);
        }
    };

    const onPaymentPreferenceContinue = async () => {
        // trackFormStartedEvent(CONTINUE); // todo
        const { paymentPreferenceType } = formState.data;
        const paymentPreferenceTypeError = !paymentPreferenceType
            ? intl.formatMessage({ id: 'payment_preference_type_error' })
            : null;
        // sendSubmitEvent(paymentPreferenceType); // todo
        const userExists = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.USER_EXISTS);

        if (paymentPreferenceTypeError) {
            saveFormErrorState(PAYMENT_PREFERENCE_TYPE, paymentPreferenceTypeError);
            sendErrorEvent(paymentPreferenceTypeError);
            return;
        }
        sessionStorage?.setItem(STORAGE_CONFIG.SESSION_STORAGE.PAYMENT_PREFERENCE_TYPE, paymentPreferenceType);

        if (userExists == 'true') {
            navigateToPath(navigate, LOOKS_LIKE);
        } else {
            // redirect to cca or pod based on user payment preference selection
            const pagePath = isPODFlow() ? POD_PATH :  CCA_PATH;
            window.location.replace(pagePath)
        }
    };

    const onContinue = async () => {
        trackFormStartedEvent(CONTINUE);
        const { email, accountType } = formState.data;
        const emailError = getEmailError(email);
        const accounttypeError = getAccountTypeError(accountType);
        sendSubmitEvent(accountType);

        try {
            if (!emailError && !accounttypeError) {
                setIsLoading(true);
                const response = await checkDuplicateProfile(email);
                if (response.status === SUCCESS_CODES.SUCCESS_CODE_200) {
                    const {
                        data: {
                            data: { userExists, userType, freeEmailDomain }
                        }
                    } = response;
                    if (
                        userExists &&
                        (userType === CREDIT || (userType === NON_CREDIT && accountType === PERSONAL_ACCOUNT))
                    ) {
                        saveFormErrorState(USER_EXISTS, createAccountErrorMsg.userAlreadyExist);
                        sendErrorEvent(createAccountErrorMsg.userAlreadyExist);
                    } else if (accountType === BUSINESS_ACCOUNT && freeEmailDomain) {
                        // show modal
                        sessionStorage?.setItem(STORAGE_CONFIG.SESSION_STORAGE.USER_EXISTS, userExists);
                        setShowFreeEmailModal(true);
                    } else {
                        sessionStorage?.setItem(STORAGE_CONFIG.SESSION_STORAGE.USER_EXISTS, userExists);
                        const response = await resendVerifyCode(email, EMAIL);
                        if (response.status === SUCCESS_CODES.SUCCESS_CODE_201) {
                            const payload = { email, accountType };
                            guidedRegDispatch({ type: CREATE_ACCOUNT_DATA, payload: payload });
                            navigateToPath(navigate, EMAIL_VERIFY);

                            sendEventsForGuidedRegistrationFormCompleted(
                                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_COMPLETED,
                                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_FORM_NAME,
                                accountType
                            );
                            setIsLoading(false);
                        } else if (response.error) {
                            throw response.error;
                        }
                    }
                } else if (response.error) {
                    throw response.error;
                }
            } else {
                saveFormErrorState(EMAIL, emailError);
                saveFormErrorState(ACCOUNT_TYPE, accounttypeError);
                sendErrorEvent(accounttypeError || emailError);
            }
            setIsLoading(false);
        } catch (error) {
            logGRError(error);
            setIsLoading(false);
        }
    };

    return {
        formState,
        handleInputChange,
        handleFocus,
        handleSubmit,
        onContinue,
        onPaymentPreferenceContinue,
        isLoading,
        setIsLoading,
        showFreeEmailModal,
        setShowFreeEmailModal,
        handleFreeEmailContinue,
        saveFormDataState
    };
};

export default useCreateAccount;
